import {
  createSlice,
  createAsyncThunk,
  createListenerMiddleware,
  isAnyOf,
} from '@reduxjs/toolkit';
import { instance } from '@app/api/axios';
import { transactionsParams, transactionsFilters } from '@utils/tables/params';
import { calculateFiltersCount } from '@utils/filters-helpers';
import { showToaster } from '@shared/ui/messages/useToaster';
import i18n from '@app/i18n';

const countFilters = (params) =>
  calculateFiltersCount(params, transactionsFilters);

export const fetchTransactions = createAsyncThunk(
  'transactions/fetchTransactions',
  async (_, { rejectWithValue, getState }) => {
    const { params, search } = getState().transactions;

    const merged = { ...transactionsParams, ...params };
    const query = {
      page: merged.page,
      per_page: merged.per_page,
      sort_by: merged.sort_by,
      sort_dir: merged.sort_dir,
      ...(search && { search }),
      ...Object.keys(transactionsFilters).reduce((acc, key) => {
        if (merged[key] || merged[key] === 0) {
          acc[key] = merged[key];
        }
        return acc;
      }, {}),
    };

    try {
      const res = await instance.get('/card/transactions', {
        params: query,
      });
      return res.data;
    } catch (err) {
      showToaster(
        'error',
        err?.response?.data?.message || i18n.t('request_error'),
      );
      return rejectWithValue(err.response?.data);
    }
  },
);

const transactionsSlice = createSlice({
  name: 'transactions',
  initialState: {
    transactions: [],
    count_total: 0,
    count_filtered: 0,
    params: transactionsParams,
    search: null,
    filtersCount: 0,
    isInitialized: false,
    isLoading: true,
  },
  reducers: {
    setTransactionsParams: (state, action) => {
      state.params = { ...state.params, ...action.payload };
      state.filtersCount = countFilters(state.params);
    },
    setTransactionsFiltersNoFetch: (state, action) => {
      const { params, search = null } = action.payload || {};
      state.params = { ...transactionsParams, ...params };
      state.search = search;
      state.filtersCount = countFilters(state.params);
    },
    setTransactionsSearch: (state, action) => {
      state.search = action.payload;
      state.params = { ...state.params, page: 1 };
      if (state.transactions?.length === 0) {
        state.isInitialized = false;
      }
    },
    resetTransactionsFilters: (state) => {
      state.params = {
        ...state.params,
        page: 1,
        ...transactionsFilters,
      };
      state.filtersCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTransactions.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchTransactions.fulfilled, (state, action) => {
      state.isInitialized = true;
      state.isLoading = false;
      state.transactions = action.payload?.data;
      state.count_total = action.payload?.count_total;
      state.count_filtered = action.payload?.count_filtered;
      state.filtersCount = countFilters(state.params);
    });
    builder.addCase(fetchTransactions.rejected, (state) => {
      state.isLoading = false;
    });
  },
});

export const {
  setTransactionsParams,
  setTransactionsFiltersNoFetch,
  setTransactionsSearch,
  resetTransactionsFilters,
} = transactionsSlice.actions;

export default transactionsSlice.reducer;

export const transactionsListener = createListenerMiddleware();

transactionsListener.startListening({
  matcher: isAnyOf(
    setTransactionsParams,
    setTransactionsSearch,
    resetTransactionsFilters,
  ),
  effect: async (_, listenerApi) => {
    await listenerApi.dispatch(fetchTransactions());
  },
});
